/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter } from 'react-router';
import { AuthGuard } from './components/AuthGuard';
import { RequirePermission } from './components/RequirePermission';
import { DashboardLayout } from './layouts/DashboardLayout';
import { NotFoundPage, RouteErrorPage } from './pages/RouteErrorPage';

// Retired for lophoc (replaced by WorkbenchPage at '/'); kept unused per plan, not deleted.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function DashboardHome() {
  const { t } = useTranslation('common');
  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-4">{t('dashboardPage.title')}</h1>
      <p className="text-muted-foreground">{t('dashboardPage.welcome')}</p>
    </div>
  );
}

// Eagerly loaded — always needed for auth flow
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';

// Lazy loaded — infrequently accessed pages
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const InviteAcceptPage = lazy(() =>
  import('./pages/auth/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
);
const ImpersonatePage = lazy(() =>
  import('./pages/ImpersonatePage').then((m) => ({ default: m.ImpersonatePage })),
);
const ImpersonationEndedPage = lazy(() =>
  import('./pages/ImpersonatePage').then((m) => ({ default: m.ImpersonationEndedPage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const BookkeepingPage = lazy(() =>
  import('./pages/bookkeeping/BookkeepingPage').then((m) => ({ default: m.BookkeepingPage })),
);
const WorkbenchPage = lazy(() =>
  import('./pages/workbench/WorkbenchPage').then((m) => ({ default: m.WorkbenchPage })),
);
const ExpensesPage = lazy(() =>
  import('./pages/bookkeeping/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const IncomePage = lazy(() =>
  import('./pages/bookkeeping/IncomePage').then((m) => ({ default: m.IncomePage })),
);
const UnmatchedPaymentsPage = lazy(() =>
  import('./pages/payments/UnmatchedPaymentsPage').then((m) => ({ default: m.UnmatchedPaymentsPage })),
);
const PayrollPage = lazy(() =>
  import('./pages/payroll/PayrollPage').then((m) => ({ default: m.PayrollPage })),
);
const AttendanceMonthPage = lazy(() =>
  import('./pages/attendance/AttendanceMonthPage').then((m) => ({ default: m.AttendanceMonthPage })),
);
const MonthlyReviewsPage = lazy(() =>
  import('./pages/reviews/MonthlyReviewsPage').then((m) => ({ default: m.MonthlyReviewsPage })),
);
const MonthlyReviewsIndexPage = lazy(() =>
  import('./pages/reviews/MonthlyReviewsIndexPage').then((m) => ({ default: m.MonthlyReviewsIndexPage })),
);

// Dashboard pages (protected)
import { ClientDetailPage } from './pages/clients/ClientDetailPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { InvoiceDetailPage } from './pages/invoices/InvoiceDetailPage';
import { InvoiceFormPage } from './pages/invoices/InvoiceFormPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { CloseMonthPage } from './pages/invoices/CloseMonthPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { SettingsIndex } from './pages/settings/SettingsIndex';
import { SettingsSection } from './pages/settings/SettingsSection';
import { SubscriptionInvoicePage } from './pages/billing/SubscriptionInvoicePage';
import { SubjectsPage } from './pages/subjects/SubjectsPage';
import { SubjectDetailPage } from './pages/subjects/SubjectDetailPage';
import { StudentsPage } from './pages/students/StudentsPage';
import { StudentDetailPage } from './pages/students/StudentDetailPage';
import { SessionsRedirect } from './pages/sessions/SessionsRedirect';
import { SessionDetailPage } from './pages/sessions/SessionDetailPage';
import { ClassesPage } from './pages/classes/ClassesPage';
import { ClassDetailPage } from './pages/classes/ClassDetailPage';
import { SchedulesRedirect } from './pages/classes/SchedulesRedirect';

const lazyFallback = (
  <div className="flex h-screen items-center justify-center bg-surface-50">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

export const router = createBrowserRouter([
  {
    // A pathless root purely to own `errorElement`: it is the net under every
    // route below, so a component that throws shows our page instead of React
    // Router's built-in developer notice — which it renders in production too.
    // The catch-all sits at the end of these children, outside AuthGuard, so an
    // unknown URL reads as "no such page" whether or not anyone is signed in,
    // instead of bouncing a logged-out visitor to the login form.
    errorElement: <RouteErrorPage />,
    children: [
  // Public auth routes
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    path: '/forgot-password',
    element: <Suspense fallback={lazyFallback}><ForgotPasswordPage /></Suspense>,
  },
  {
    path: '/reset-password',
    element: <Suspense fallback={lazyFallback}><ResetPasswordPage /></Suspense>,
  },
  {
    path: '/verify-email',
    element: <Suspense fallback={lazyFallback}><VerifyEmailPage /></Suspense>,
  },
  {
    path: '/invite/accept',
    element: <Suspense fallback={lazyFallback}><InviteAcceptPage /></Suspense>,
  },
  {
    path: '/impersonate',
    element: <Suspense fallback={lazyFallback}><ImpersonatePage /></Suspense>,
  },
  {
    path: '/impersonate/ended',
    element: <Suspense fallback={lazyFallback}><ImpersonationEndedPage /></Suspense>,
  },

  // Protected routes
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/onboarding',
        element: <Suspense fallback={lazyFallback}><OnboardingPage /></Suspense>,
      },

      // Dashboard (with sidebar layout)
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Suspense fallback={lazyFallback}><WorkbenchPage /></Suspense>,
          },
          {
            path: 'clients',
            element: <RequirePermission perm="manage:clients"><ClientsPage /></RequirePermission>,
          },
          {
            path: 'clients/:id',
            element: <RequirePermission perm="manage:clients"><ClientDetailPage /></RequirePermission>,
          },
          {
            path: 'bookkeeping',
            element: <RequirePermission perm="manage:bookkeeping"><Suspense fallback={lazyFallback}><BookkeepingPage /></Suspense></RequirePermission>,
          },
          {
            path: 'bookkeeping/expenses',
            element: <RequirePermission perm="manage:bookkeeping"><Suspense fallback={lazyFallback}><ExpensesPage /></Suspense></RequirePermission>,
          },
          {
            path: 'bookkeeping/income',
            element: <RequirePermission perm="manage:bookkeeping"><Suspense fallback={lazyFallback}><IncomePage /></Suspense></RequirePermission>,
          },
          {
            path: 'payroll',
            element: <Suspense fallback={lazyFallback}><PayrollPage /></Suspense>,
          },
          { path: 'invoices', element: <RequirePermission perm="manage:invoices"><InvoicesPage /></RequirePermission> },
          {
            path: 'payments/unmatched',
            element: <RequirePermission perm="manage:invoices"><Suspense fallback={lazyFallback}><UnmatchedPaymentsPage /></Suspense></RequirePermission>,
          },
          { path: 'invoices/new', element: <RequirePermission perm="manage:invoices"><InvoiceFormPage /></RequirePermission> },
          { path: 'invoices/close-month', element: <RequirePermission perm="manage:invoices"><CloseMonthPage /></RequirePermission> },
          { path: 'invoices/:id/edit', element: <RequirePermission perm="manage:invoices"><InvoiceFormPage /></RequirePermission> },
          { path: 'invoices/:id', element: <RequirePermission perm="manage:invoices"><InvoiceDetailPage /></RequirePermission> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <SettingsIndex /> },
              { path: ':section', element: <SettingsSection /> },
            ],
          },
          // Outside SettingsLayout (which drops the org group for MEMBER), so it needs its own guard (THI-136).
          { path: 'settings/billing/invoices/:id', element: <RequirePermission perm="manage:settings"><SubscriptionInvoicePage /></RequirePermission> },
          { path: 'subjects', element: <SubjectsPage /> },
          { path: 'subjects/:id', element: <SubjectDetailPage /> },
          {
            path: 'students',
            element: <RequirePermission perm="manage:students"><StudentsPage /></RequirePermission>,
          },
          { path: 'students/:id', element: <StudentDetailPage /> },
          { path: 'sessions', element: <SessionsRedirect /> },
          { path: 'sessions/:id', element: <SessionDetailPage /> },
          // THI-267: "Thời khóa biểu" -> "Lớp học". Neither route is wrapped
          // in RequirePermission — MEMBER reads both; write affordances gate
          // per-action with useCan('manage:slots') + AdminOnlyTooltip.
          { path: 'classes', element: <ClassesPage /> },
          { path: 'classes/:id', element: <ClassDetailPage /> },
          // `?template=x` deep links exist — the redirect preserves the full
          // query string (and pins `?tab=grid`, since /schedules only ever
          // showed the grid). Bookmarks and old nav history still resolve.
          { path: 'schedules', element: <SchedulesRedirect /> },
          // No sidebar entry, deliberately (nav stays at 9): reached from the
          // workbench week strip and from a student's "Đăng ký học" header.
          {
            path: 'attendance',
            element: <Suspense fallback={lazyFallback}><AttendanceMonthPage /></Suspense>,
          },
          // No sidebar entry itself (the INDEX page below has one): reached
          // from a subject's slot dialog, the workbench "Cần chú ý" card, and
          // the index page's per-subject rows.
          {
            path: 'subjects/:id/monthly-reviews',
            element: <Suspense fallback={lazyFallback}><MonthlyReviewsPage /></Suspense>,
          },
          // Sidebar entry: "Nhận xét tháng" under Học viên.
          {
            path: 'monthly-reviews',
            element: <Suspense fallback={lazyFallback}><MonthlyReviewsIndexPage /></Suspense>,
          },
        ],
      },
    ],
  },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
